import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const EMPTY_TOURNAMENT = { name: "", date: "", start_time: "08:00", end_time: "12:00", price_per_person: "500", prize: "", max_teams: "8", match_duration: "30", description: "" };

export const tournamentToForm = (t) => ({
  name: t.name, date: t.date, start_time: t.start_time, end_time: t.end_time,
  price_per_person: String(t.price_per_person), prize: t.prize || "", max_teams: String(t.max_teams),
  match_duration: String(t.match_duration || 30), description: t.description || "",
});

export default function TournamentForm({ initial = EMPTY_TOURNAMENT, tournamentId, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.date) { toast.error("Name and date are required"); return; }
    setSaving(true);
    const payload = { ...form, price_per_person: parseFloat(form.price_per_person) || 0, max_teams: parseInt(form.max_teams) || 8, match_duration: parseInt(form.match_duration) || 30 };
    try {
      const res = tournamentId ? await api.put(`/tournaments/${tournamentId}`, payload) : await api.post("/tournaments", payload);
      toast.success(tournamentId ? "Tournament updated" : "Tournament created");
      onSaved?.(res.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="tournament-form">
      <div><Label>Tournament name</Label><Input value={form.name} onChange={set("name")} placeholder="D&D Sunday Open" className="rounded-xl" data-testid="tournament-name-input" /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} className="rounded-xl" data-testid="tournament-date-input" /></div>
        <div><Label>Start</Label><Input type="time" step="3600" value={form.start_time} onChange={set("start_time")} className="rounded-xl" data-testid="tournament-start-input" /></div>
        <div><Label>End</Label><Input type="time" step="3600" value={form.end_time} onChange={set("end_time")} className="rounded-xl" data-testid="tournament-end-input" /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Price / person (Rs)</Label><Input type="number" min="0" value={form.price_per_person} onChange={set("price_per_person")} className="rounded-xl" data-testid="tournament-price-input" /></div>
        <div><Label>Max teams</Label><Input type="number" min="2" max="32" value={form.max_teams} onChange={set("max_teams")} className="rounded-xl" data-testid="tournament-teams-input" /></div>
        <div><Label>Match length (min)</Label><Input type="number" min="10" step="5" value={form.match_duration} onChange={set("match_duration")} className="rounded-xl" data-testid="tournament-duration-input" /></div>
      </div>
      <div><Label>Prize</Label><Input value={form.prize} onChange={set("prize")} placeholder="Rs 5,000 + trophies" className="rounded-xl" data-testid="tournament-prize-input" /></div>
      <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} className="rounded-xl min-h-[70px]" placeholder="Format, rules, what's included..." data-testid="tournament-desc-input" /></div>
      <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white" data-testid="tournament-form-submit">
        {saving ? "Saving..." : tournamentId ? "Save changes" : "Create tournament"}
      </Button>
    </div>
  );
}
